/* eslint-disable react/prop-types, @typescript-eslint/no-unsafe-assignment, 	@typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument, @typescript-eslint/unbound-method  */
import React from 'react';
import { format, isValid, parse } from 'date-fns';
import { FormInput, CustomField, FormTextArea } from 'components/next/components/form/input';
import LanguageSelector from 'components/common/next/languageSelector';
import { Checkbox } from 'components/next/components/checkbox';
import type { ContentField, DeliveryLanguage } from 'components/next/types';
import * as _ from 'lodash';
import './fieldEditor.scss';
import Dropdown, { DropdownItem } from '../dropdown';
import InputImage from 'components/common/next/form/inputImage';
import InputStaff from 'components/common/next/form/inputStaff';
import InputServices from 'components/common/next/form/inputServices';
import { ReactComponent as IconUp } from '@kesko/icons/action/icon-push_up.svg';
import { ReactComponent as IconDown } from '@kesko/icons/action/icon-push_down.svg';
import { ReactComponent as IconDelete } from '@kesko/icons/action/icon-delete.svg';
import BttProductSelect from 'components/common/next/offerEditor/bttProductSelect';
import InputNumber from 'components/common/next/form/inputNumber';
import { ContentFieldType, BusinessType } from 'enums/common';
import { croppingOptions, ensureString, applyCrop } from 'utils/helpers';
import { ConceptLanguageContext } from 'providers/ConceptLanguageProvider';
import BttStaffSelect from 'components/common/next/offerEditor/bttStaffSelect';
import BttServicesSelect from 'components/common/next/offerEditor/bttServicesSelect';
import InputText from 'components/common/next/form/inputText';

interface FieldEditorProps {
  index: number;
  preventOverWrites?: boolean;
  contentField: ContentField;
  allContentFields?: ContentField[];
  update?(c: ContentField): void;
  deleteField(id: string): void;
  changeOrder(o: number, s: boolean): void;
  dropdownData?: DropdownItem[];
  targetGroupType?: BusinessType;
}

interface FieldEditorState {
  language: DeliveryLanguage;
}

const defaultCropping = croppingOptions[0];

export default class FieldEditor extends React.Component<FieldEditorProps, FieldEditorState> {
  static contextType = ConceptLanguageContext;
  constructor(props: FieldEditorProps) {
    super(props);

    this.onTypeChange = this.onTypeChange.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.renderPropEditor = this.renderPropEditor.bind(this);
    this.getPropValue = this.getPropValue.bind(this);
  }

  handleUpdate(fieldName: string, value: any) {
    const { update } = this.props;
    const contentField = { ...this.props.contentField };
    update(_.set(contentField, fieldName, value));
  }
  handleImageUpdate(fieldName: string, src: any) {
    const { update } = this.props;
    const contentField = { ...this.props.contentField };
    const url = new URL(src);
    const hasQueryParams = url.searchParams.toString().length > 0;
    const value = hasQueryParams ? src : `${src}?${defaultCropping.queryParams}`;
    update(_.set(contentField, fieldName, value));
  }
  getNextIdentifierNumber(baseIdentifier: string): number {
    const { allContentFields } = this.props;
    if (!allContentFields || allContentFields.length === 0) {
      return 1;
    }

    // Find all identifiers that match the pattern (e.g., "bttStaffInformation1", "bttStaffInformation2")
    const pattern = new RegExp(`^${baseIdentifier}(\\d+)$`);
    const numbers = allContentFields
      .map((field) => {
        const match = field.identifier?.match(pattern);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((num) => num > 0);

    // Return the highest number + 1, or 1 if no numbered identifiers exist
    return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  }

  onTypeChange(type: string) {
    const { update } = this.props;
    let contentField = { ...this.props.contentField };
    contentField = _.set(contentField, 'type', type);

    // Set default identifier and title when bttStaffInformation is selected
    if (process.env.REACT_APP_STAFF_SELECTION === 'true' && type === 'bttStaffInformation') {
      const nextNumber = this.getNextIdentifierNumber('bttStaffInformation');
      const staffIdentifier = `bttStaffInformation${nextNumber}`;
      contentField = _.set(contentField, 'identifier', staffIdentifier);
      contentField = _.set(contentField, `title.${this.context.language}`, 'Asiantuntijamme');
      contentField = _.set(
        contentField,
        `description.${this.context.language}`,
        'Lisää tähän haluamasi kuvaus asiantuntijoista.',
      );
    } else if (process.env.REACT_APP_STAFF_SELECTION === 'true' && type === 'bttServices') {
      const nextNumber = this.getNextIdentifierNumber('bttServices');
      const servicesIdentifier = `bttServices${nextNumber}`;
      contentField = _.set(contentField, 'identifier', servicesIdentifier);
      contentField = _.set(contentField, `title.${this.context.language}`, 'Palvelumme');
      contentField = _.set(
        contentField,
        `description.${this.context.language}`,
        'Lisää tähän haluamasi kuvaus palveluista.',
      );
    }

    update(contentField);
  }

  getPropValue(propName) {
    const { contentField } = this.props;
    if (!contentField || !contentField.props) {
      return '';
    }
    return contentField.props[propName] || '';
  }

  renderPropEditor() {
    const propFields = this.getPropFields();
    if (!propFields) {
      return null;
    }
    return (
      <div className="field-editor__props">
        <header>
          <h3>Content field additional properties</h3>
        </header>
        {propFields}
      </div>
    );
  }

  getPropFields() {
    const { contentField } = this.props;
    const { language } = this.context as React.ContextType<typeof ConceptLanguageContext>;

    const minLength = (
      <FormInput
        key="minLength"
        type="number"
        label="Min length"
        value={this.getPropValue('minLength')}
        additionalClasses="half-wide"
        handleChange={(e) => this.handleUpdate('props.minLength', e.target.value)}
      />
    );
    const maxLength = (
      <FormInput
        key="maxLength"
        type="number"
        label="Max length"
        value={this.getPropValue('maxLength')}
        additionalClasses="half-wide"
        handleChange={(e) => this.handleUpdate('props.maxLength', e.target.value)}
      />
    );
    const pattern = (
      <FormInput
        key="pattern"
        label="Pattern"
        value={this.getPropValue('pattern')}
        handleChange={(e) => this.handleUpdate('props.pattern', e.target.value)}
      />
    );
    const placeholder = (
      <FormInput
        key="placeholder"
        label="Placeholder"
        value={this.getPropValue('placeholder')}
        handleChange={(e) => this.handleUpdate('props.placeholder', e.target.value)}
      />
    );
    const allowedTags = (
      <FormInput
        key="allowedTags"
        label="Allowed tags"
        value={this.getPropValue('allowedTags')}
        handleChange={(e) => this.handleUpdate('props.minLength', e.target.value)}
        detail="Insert allowed tags separated by a comma"
      />
    );
    const minDate = (
      <FormInput
        key="minDate"
        type="date"
        label="Min"
        value={this.getPropValue('maxDate')}
        handleChange={(e) => this.handleUpdate('maxDate', e.target.value)}
      />
    );
    const maxDate = (
      <FormInput
        key="maxDate"
        type="date"
        label="Max"
        value={this.getPropValue('maxDate')}
        handleChange={(e) => this.handleUpdate('maxDate', e.target.value)}
      />
    );
    const options = (
      <FormInput
        key="options"
        label="Options"
        value={this.getPropValue('options')}
        handleChange={(e) => this.handleUpdate('props.options', e.target.value)}
        detail="Insert possible options separated by a comma"
      />
    );
    const defaultValue = this.getContent('defaultValue');
    const isDefaultValueObject = typeof defaultValue === 'object';
    const defaultImage = (
      <CustomField key={`defaultImage-${contentField.id}`} label="Field default image">
        <InputImage
          value={isDefaultValueObject ? { src: defaultValue.src, text: defaultValue.text } : null}
          onChange={({ src, text }) => this.handleUpdate(`defaultValue.${language}`, { src, text })}
        />
      </CustomField>
    );
    const bttProductDefaultImage = (
      <CustomField key={`bttProductDefaultImage-${contentField.id}`} label="Product default image">
        <InputImage
          value={
            isDefaultValueObject
              ? { src: defaultValue.src || '', text: defaultValue.text || '', imageUrls: defaultValue.imageUrls || [] }
              : { src: '', text: '', imageUrls: [] }
          }
          onChange={({ src, imageUrls }) => {
            const updatedProduct = isDefaultValueObject
              ? { ...defaultValue, src: src || '', imageUrls: imageUrls || [] }
              : { src: '', imageUrls: [] };
            this.handleUpdate(`defaultValue.${language}`, updatedProduct);
          }}
          dropdown={{
            lang: 'en',
          }}
        />
        {isDefaultValueObject && defaultValue.imageUrls && defaultValue.imageUrls.length > 1 ? (
          <div className="btt-product-image-option-list">
            {defaultValue.imageUrls.map((imageUrl) => (
              <div key={imageUrl} className="btt-product-image-option-item">
                <img
                  src={`${imageUrl}?h=100&fm=png`}
                  alt={imageUrl}
                  onClick={() => this.handleImageUpdate(`defaultValue.${language}.src`, imageUrl)}
                />
              </div>
            ))}
          </div>
        ) : (
          <></>
        )}
      </CustomField>
    );

    const bttStaffInformationDefaultStaff =
      process.env.REACT_APP_STAFF_SELECTION === 'true' ? (
        <CustomField key={`bttStaffInformationDefaultStaff-${contentField.id}`} label="Staff information default">
          <InputStaff
            name={contentField.identifier}
            value={
              isDefaultValueObject && defaultValue.name
                ? {
                    title: defaultValue.title || '',
                    name: defaultValue.name || '',
                    email: defaultValue.email || '',
                    links: defaultValue.links || [],
                    phoneNumber: defaultValue.phoneNumber || [],
                    imageUrl: defaultValue.imageUrl || '',
                    imageName: defaultValue.imageName || '',
                    phoneSelected: defaultValue.phoneSelected || '',
                    showBookingButton: defaultValue.showBookingButton ?? true,
                    linkURLSelected: defaultValue.linkURLSelected || '',
                    linkTextSelected: defaultValue.linkTextSelected || '',
                  }
                : { name: '', email: '', links: [], phoneNumber: [] }
            }
            onChange={(staffInformation) => {
              this.handleUpdate(`defaultValue.${language}`, staffInformation);
            }}
          />
        </CustomField>
      ) : null;

    const bttServicesDefault =
      process.env.REACT_APP_STAFF_SELECTION === 'true' ? (
        // TODO: What is this section and what should be code here?
        <CustomField key={`bttServicesDefaultName-${contentField.id}`} label="Services default name">
          <InputServices
            name={contentField.identifier}
            targetGroupType={this.props.targetGroupType}
            value={
              isDefaultValueObject && defaultValue.type
                ? {
                    title: defaultValue.title || '',
                    type: defaultValue.type || '',
                    info: defaultValue.info || '',
                    links: defaultValue.links || [],
                    linkTextSelected: defaultValue.linkTextSelected || '',
                    linkURLSelected: defaultValue.linkURLSelected || '',
                  }
                : {
                    title: '',
                    type: '',
                    info: '',
                    links: [],
                    linkTextSelected: '',
                    linkURLSelected: '',
                  }
            }
            onChange={(services) => this.handleUpdate(`defaultValue.${language}`, services)}
          />
        </CustomField>
      ) : null;

    const imageText = (
      <FormInput
        key={`image-text-${contentField.id}`}
        label={`Default image text - ${language}`}
        value={isDefaultValueObject ? defaultValue.text : ''}
        handleChange={(e) => this.handleUpdate(`defaultValue.${language}.text`, e.target.value)}
      />
    );

    const imageWithText = (
      <Checkbox
        key={`image-withText-${contentField.id}`}
        id={`image-withText-${contentField.id}`}
        label="Enable textfield for image"
        checked={this.getPropValue('withText')}
        handleClick={(e) => this.handleUpdate('props.withText', e.target.checked)}
      />
    );

    if (contentField.type === ContentFieldType.text) {
      return [minLength, maxLength, pattern, placeholder];
    }
    if ((contentField.type.toLowerCase() as ContentFieldType) === ContentFieldType.richtext) {
      return [minLength, maxLength, pattern, placeholder, allowedTags];
    }
    if (contentField.type === ContentFieldType.date) {
      return [minDate, maxDate];
    }
    if (contentField.type === ContentFieldType.select) {
      return options;
    }
    if (contentField.type === ContentFieldType.image) {
      return _.get(contentField, ['props', 'withText'], false) === true
        ? [defaultImage, imageWithText, imageText]
        : [defaultImage, imageWithText];
    }
    if (contentField.type === ContentFieldType.bttPromotedProduct) {
      return [bttProductDefaultImage];
    }
    if (contentField.type === ContentFieldType.bttStaffInformation) {
      return [bttStaffInformationDefaultStaff];
    }
    if (contentField.type === ContentFieldType.bttServices) {
      return [bttServicesDefault];
    }
    return null;
  }

  getContent = (propertyName: string): string | Record<string, any> => {
    const { contentField } = this.props;
    const { language } = this.context as React.ContextType<typeof ConceptLanguageContext>;
    return _.get(contentField, `${propertyName}.${language}`, '');
  };

  render() {
    const { contentField, preventOverWrites, index, changeOrder, deleteField } = this.props;
    const { language, setLanguage } = this.context as React.ContextType<typeof ConceptLanguageContext>;
    const types: DropdownItem[] = Object.keys(ContentFieldType)
      .filter((key) => {
        if (process.env.REACT_APP_STAFF_SELECTION !== 'true') {
          return key !== 'bttStaffInformation' && key !== 'bttServices';
        }
        return true;
      })
      .map((key) => ({
        name: key,
        value: ContentFieldType[key],
      }));
    const selectedType: DropdownItem = types.find((t) => t.name === contentField.type);

    return (
      <section className="editor-section field-editor">
        <header className="editor-section__header">
          <div className="title-row">
            <h3 className="section-title">{this.getContent('title')}</h3>
            <div className="title-row__buttons">
              <LanguageSelector
                language={language}
                languages={['fi', 'sv']}
                toggleLanguage={(language: DeliveryLanguage) => setLanguage(language)}
              />
              <button onClick={() => changeOrder(index, false)}>
                <IconDown />
              </button>
              <button onClick={() => changeOrder(index, true)}>
                <IconUp />
              </button>
              <button className="delete-field delete-button" onClick={() => deleteField(contentField.id)}>
                <IconDelete />
              </button>
            </div>
          </div>
        </header>
        <div className="field-details">
          <span>Type: {ContentFieldType[contentField.type] || 'unknown'}</span>
          <span>HTML identifier: {contentField.identifier || 'unknown'}</span>
        </div>
        {!preventOverWrites && (
          <React.Fragment>
            <FormInput
              label="HTML identifier"
              value={contentField.identifier}
              handleChange={(e) => this.handleUpdate('identifier', e.target.value)}
              detail="The name used to refer to this field in HTML content. Note: identifier can not contain spaces."
              required={true}
            />
            <FormInput
              label={`Title - ${language}`}
              value={this.getContent('title') as string}
              handleChange={(e) => this.handleUpdate(`title.${language}`, e.target.value)}
              additionalClasses="half-wide"
            />
            <FormTextArea
              label={`Field description - ${language}`}
              value={this.getContent('description') as string}
              handleChange={(e) => this.handleUpdate(`description.${language}`, e.target.value)}
            />
            {(() => {
              switch (contentField.type) {
                case 'richtext': {
                  return (
                    <FormTextArea
                      label={`Default value - ${language}`}
                      value={this.getContent('defaultValue') as string}
                      handleChange={(e) => this.handleUpdate(`defaultValue.${language}`, e.target.value)}
                    />
                  );
                }
                case 'image':
                  return null;
                case 'select': {
                  const defaultSelectValue = this.getContent('defaultValue');
                  const values: DropdownItem[] = _.map(
                    _.compact(this.getPropValue('options').split(',')),
                    (option) =>
                      ({
                        name: option,
                        value: option,
                      }) as DropdownItem,
                  );

                  const selectedValue: DropdownItem = values.find((t) => t.value === defaultSelectValue);

                  return (
                    <CustomField additionalClasses="form-contro" label={`Default value - ${language}`}>
                      <Dropdown
                        data={values}
                        notSelectedText="Pick a default value"
                        selectedItem={selectedValue}
                        select={(value) => this.handleUpdate(`defaultValue.${language}`, value)}
                      />
                    </CustomField>
                  );
                }
                case 'date': {
                  const defaultDateValue = this.getContent('defaultValue') as string;
                  const tryFormatDateFromTo = (dateString: string, fromFormat: string, toFormat: string): string => {
                    const parsedDate = parse(dateString, fromFormat, new Date());
                    if (!isValid(parsedDate)) {
                      return '';
                    }
                    return format(parsedDate, toFormat);
                  };
                  return (
                    <FormInput
                      type="date"
                      label={`Default value - ${language}`}
                      value={tryFormatDateFromTo(defaultDateValue, 'd.M.yyyy', 'yyyy-MM-dd')}
                      handleChange={(e) => {
                        const value = e.target.value;
                        const newDefaultValue = tryFormatDateFromTo(value, 'yyyy-MM-dd', 'd.M.yyyy');
                        this.handleUpdate(`defaultValue.${language}`, newDefaultValue);
                      }}
                    />
                  );
                }
                case 'link': {
                  const defaultValue = this.getContent('defaultValue');
                  const isDefaultValueObject = typeof defaultValue === 'object';
                  return (
                    <React.Fragment>
                      <FormInput
                        label={`Default link text - ${language}`}
                        placeholder="Kesko"
                        value={isDefaultValueObject ? defaultValue.text : ''}
                        handleChange={(e) => this.handleUpdate(`defaultValue.${language}.text`, e.target.value)}
                      />
                      <FormInput
                        label={`Default link URL - ${language}`}
                        placeholder="https://kesko.fi/"
                        value={isDefaultValueObject ? defaultValue.href : ''}
                        handleChange={(e) => this.handleUpdate(`defaultValue.${language}.href`, e.target.value)}
                      />
                    </React.Fragment>
                  );
                }
                case 'bttPromotedProduct': {
                  const defaultValue = this.getContent('defaultValue');
                  const isDefaultValueObject = typeof defaultValue === 'object';
                  const isOnLoyaltySale = isDefaultValueObject ? defaultValue.isOnLoyaltySale : false;
                  const showTextBlock = isDefaultValueObject ? defaultValue.showTextBlock : false;
                  const textBlock = isDefaultValueObject ? defaultValue.textBlock : null;
                  return (
                    <React.Fragment>
                      <label>Search for product</label>
                      <BttProductSelect
                        closeSearchOnSelect={true}
                        onSelect={(product) => {
                          const croppedImgUrl = applyCrop(product.image, ensureString(defaultCropping.queryParams));
                          const updatedProduct = {
                            ...product,
                            src: croppedImgUrl,
                            imageUrls: product.imageUrls,
                            normalPriceVat0: null,
                            normalPriceVat24: product.basePrice,
                            salePriceVat24: product.isOnSale ? product.price : null,
                            loyaltyPrice: product.isOnLoyaltySale ? product.loyaltyPrice.price : null,
                            productNumber: product.productId,
                            omnibus: null,
                            textBlock: textBlock,
                            showTextBlock: showTextBlock,
                          };
                          this.handleUpdate(`defaultValue.${language}`, updatedProduct);
                        }}
                      />

                      <FormInput
                        label={`Product title - ${language}`}
                        placeholder="Product title"
                        value={isDefaultValueObject ? defaultValue.title : ''}
                        handleChange={(e) => this.handleUpdate(`defaultValue.${language}.title`, e.target.value)}
                        required={true}
                      />

                      <FormTextArea
                        label={`Product description - ${language}`}
                        rows={2}
                        placeholder="Product description"
                        value={isDefaultValueObject ? defaultValue.description : ''}
                        handleChange={(e) => this.handleUpdate(`defaultValue.${language}.description`, e.target.value)}
                      />

                      <InputNumber
                        name={`normalPriceVat24`}
                        label="Product normal price in € VAT 25,5%"
                        value={isDefaultValueObject ? defaultValue.normalPriceVat24 : ''}
                        onChange={(val) => {
                          this.handleUpdate(`defaultValue.${language}.normalPriceVat24`, val);
                        }}
                        allowEmptyFormatting
                        decimalScale={2}
                        min={0}
                        suffix={`€`}
                      />

                      <FormInput
                        label={`Pricing unit, normal price - ${language}`}
                        placeholder="Pricing unit, normal price"
                        value={isDefaultValueObject ? defaultValue.pricingUnit : ''}
                        handleChange={(e) => this.handleUpdate(`defaultValue.${language}.pricingUnit`, e.target.value)}
                      />

                      <InputNumber
                        name={`normalPriceVat0`}
                        label="Product normal price in € VAT 0% (For B2B-deliveries)"
                        value={isDefaultValueObject ? defaultValue.normalPriceVat0 : ''}
                        onChange={(val) => {
                          this.handleUpdate(`defaultValue.${language}.normalPriceVat0`, val);
                        }}
                        allowEmptyFormatting
                        decimalScale={2}
                        min={0}
                        suffix={`€`}
                      />

                      <InputNumber
                        name={`salePriceVat24`}
                        label="Product sale price in € VAT 25,5% (VAT 0% for B2B-deliveries)"
                        value={isDefaultValueObject ? defaultValue.salePriceVat24 : ''}
                        onChange={(val) => {
                          this.handleUpdate(`defaultValue.${language}.salePriceVat24`, val);
                        }}
                        allowEmptyFormatting
                        decimalScale={2}
                        min={0}
                        suffix={`€`}
                      />

                      <FormInput
                        label={`Pricing unit, sale price - ${language}`}
                        placeholder="Pricing unit, sale price"
                        value={isDefaultValueObject ? defaultValue.pricingUnitSale : ''}
                        handleChange={(e) =>
                          this.handleUpdate(`defaultValue.${language}.pricingUnitSale`, e.target.value)
                        }
                      />

                      <Checkbox
                        label="Is on Plussa sale?"
                        id={`product${index}-isOnLoyaltySale-${language}`}
                        checked={isOnLoyaltySale}
                        handleClick={(e) =>
                          this.handleUpdate(`defaultValue.${language}.isOnLoyaltySale`, e.target.checked)
                        }
                      />

                      <InputNumber
                        name={`loyaltyPrice`}
                        label={`Product plussa sale price in € VAT 25,5% - ${language}`}
                        value={isDefaultValueObject ? defaultValue.loyaltyPrice : ''}
                        onChange={(val) => {
                          this.handleUpdate(`defaultValue.${language}.loyaltyPrice`, val);
                        }}
                        allowEmptyFormatting
                        decimalScale={2}
                        min={0}
                        suffix={`€`}
                        readOnly={!isOnLoyaltySale}
                      />

                      <InputNumber
                        name={`omnibus`}
                        label={`Omnibus price - ${language}`}
                        value={isDefaultValueObject ? defaultValue.omnibus : ''}
                        onChange={(e) => this.handleUpdate(`defaultValue.${language}.omnibus`, e)}
                        allowEmptyFormatting
                        decimalScale={2}
                        min={0}
                        suffix={`€`}
                      />

                      <FormInput
                        label={`Product EAN - ${language}`}
                        placeholder="6405422918614"
                        value={isDefaultValueObject ? defaultValue.ean : ''}
                        handleChange={(e) => this.handleUpdate(`defaultValue.${language}.ean`, e.target.value)}
                      />

                      <FormInput
                        label={`Product number - ${language}`}
                        placeholder="500987338"
                        value={isDefaultValueObject ? defaultValue.productNumber : ''}
                        handleChange={(e) =>
                          this.handleUpdate(`defaultValue.${language}.productNumber`, e.target.value)
                        }
                      />

                      <FormInput
                        label={`Link to product page - ${language}`}
                        placeholder="https://www.k-rauta.fi/tuote/akkuporakone-makita/88381754965"
                        value={isDefaultValueObject ? defaultValue.url : ''}
                        handleChange={(e) => this.handleUpdate(`defaultValue.${language}.url`, e.target.value)}
                      />

                      <FormInput
                        label={`Image text block - ${language}`}
                        placeholder="Lisätieto"
                        value={isDefaultValueObject ? defaultValue.textBlock : ''}
                        handleChange={(e) => this.handleUpdate(`defaultValue.${language}.textBlock`, e.target.value)}
                      />

                      <Checkbox
                        label="Show image text block"
                        id={`product${index}-showTextBlock-${language}`}
                        checked={showTextBlock}
                        handleClick={(e) =>
                          this.handleUpdate(`defaultValue.${language}.showTextBlock`, e.target.checked)
                        }
                      />
                    </React.Fragment>
                  );
                }
                case 'bttStaffInformation': {
                  // Staff selection is handled in getPropFields() via InputStaff component
                  return null;
                }
                case 'bttServices': {
                  // TODO:  Render staff info editor, e.g. InputStaff
                  return null;
                }
                case 'text':
                default: {
                  return (
                    <FormInput
                      label={`Default value - ${language}`}
                      value={this.getContent('defaultValue') as string}
                      handleChange={(e) => this.handleUpdate(`defaultValue.${language}`, e.target.value)}
                    />
                  );
                }
              }
            })()}
            {!preventOverWrites && (
              <CustomField additionalClasses="half-wide" label="Field type">
                <Dropdown
                  data={types}
                  notSelectedText="Pick a type"
                  selectedItem={selectedType}
                  select={this.onTypeChange}
                />
              </CustomField>
            )}
            <CustomField detail="Make field mandatory?" additionalClasses="half-wide">
              <Checkbox
                id={`field-${contentField.id}-required`}
                label="Required field"
                checked={contentField.required}
                handleClick={(e) => this.handleUpdate('required', e.target.checked)}
              />
            </CustomField>
            <CustomField detail="Allow user to change field value?" additionalClasses="half-wide">
              <Checkbox
                id={`field-${contentField.id}-readonly`}
                label="Read only"
                checked={contentField.readOnly}
                handleClick={(e) => this.handleUpdate('readOnly', e.target.checked)}
              />
            </CustomField>
            {this.renderPropEditor()}
          </React.Fragment>
        )}
      </section>
    );
  }
}
